import { useRef, useState, useEffect } from 'react';
import { Typography, Button, Space, Empty, Input } from 'antd';
import {
  EyeOutlined, EyeInvisibleOutlined, DeleteOutlined, LockOutlined, UnlockOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import useDashboardStore from '../../stores/dashboardStore';
import { widgetRegistry } from '../../registry/widgetRegistry';
import { dropIndexFor, reorderByDrop } from './layerDrag';

const { Text } = Typography;

function zOf(c: any) {
  return (c.config?.zIndex ?? c.style?.zIndex ?? c.base?.zIndex ?? 1) as number;
}

export default function LayerPanel2D() {
  const {
    components, selectedComponentId, selectComponent, reorderLayers,
    updateComponent, removeComponent,
  } = useDashboardStore();
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  // 2026-10-13（用户反馈）：左键按住拖动排序——改为指针拖拽（HTML5 dnd 在嵌套面板/文本选择下不可靠）
  const [drag, setDrag] = useState<{ id: string; overIndex: number } | null>(null);
  const dragRef = useRef<{ id: string; startY: number; active: boolean; overIndex: number } | null>(null);
  const suppressClickRef = useRef(false);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const nameOf = (c: any) => ((c.config?.name as string) || widgetRegistry.getLabel(c.type)) as string;

  // 过滤 + 排序：从上到下 = zIndex 从大到小
  const sorted = [...components]
    .sort((a, b) => zOf(b) - zOf(a))
    .filter((c) => nameOf(c).toLowerCase().includes(search.toLowerCase()));
  // 供全局鼠标监听读取最新列表（避免闭包过期）
  const sortedRef = useRef(sorted);
  sortedRef.current = sorted;

  // 双向联动：画布选中 → 滚动并高亮图层条目
  useEffect(() => {
    if (selectedComponentId && rowRefs.current[selectedComponentId]) {
      rowRefs.current[selectedComponentId]?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [selectedComponentId]);

  // 指针拖拽全局监听（左键按住 → 移动超过阈值激活 → 依据行中线计算插入位 → 松手重排）
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const st = dragRef.current;
      if (!st) return;
      if (!st.active) {
        if (Math.abs(e.clientY - st.startY) < 4) return;
        st.active = true;
        suppressClickRef.current = true;
        document.body.style.userSelect = 'none';
      }
      const rects = sortedRef.current.map((c) => {
        const r = rowRefs.current[c.id]?.getBoundingClientRect();
        return { top: r?.top ?? 0, bottom: r?.bottom ?? 0 };
      });
      st.overIndex = dropIndexFor(e.clientY, rects);
      setDrag({ id: st.id, overIndex: st.overIndex });
      e.preventDefault();
    };
    const onUp = () => {
      const st = dragRef.current;
      dragRef.current = null;
      document.body.style.userSelect = '';
      if (st?.active) {
        const list = sortedRef.current.map((c) => c.id);
        const next = reorderByDrop(list, st.id, st.overIndex);
        if (next.join('|') !== list.join('|')) reorderLayers(next);
      }
      setDrag(null);
      // 拖拽后抑制一次 click（避免误选）
      if (st?.active) setTimeout(() => { suppressClickRef.current = false; }, 0);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
  }, [reorderLayers]);

  if (components.length === 0) {
    return (
      <div style={{ padding: 12, color: '#888' }}>
        <Empty description="暂无组件" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </div>
    );
  }

  const toggleLock = (c: any) => {
    const locked = c.config?.locked === true;
    updateComponent(c.id, { config: { ...(c.config || {}), locked: !locked } });
  };

  const toggleVisible = (c: any) => {
    const visible = (c.config?.visible ?? c.base?.visible ?? true) === true;
    updateComponent(c.id, { config: { ...(c.config || {}), visible: !visible } });
  };

  const commitRename = (id: string) => {
    if (renameValue.trim()) updateComponent(id, { config: { ...(components.find((x) => x.id === id)?.config || {}), name: renameValue.trim() } });
    setEditingId(null);
  };

  /** 在按钮/输入框上按下不触发拖拽（眼睛/锁定/删除/重命名输入）；搜索过滤时禁用拖拽避免"隐藏行被排到底" */
  const onRowMouseDown = (e: React.MouseEvent, id: string) => {
    if (e.button !== 0) return;
    const el = e.target as HTMLElement;
    if (el.closest('button,input,textarea,.ant-input')) return;
    if (search.trim()) return;
    dragRef.current = { id, startY: e.clientY, active: false, overIndex: 0 };
  };

  return (
    <div style={{ padding: 8 }}>
      <Text strong style={{ fontSize: 12, color: '#e0e0e0', display: 'block', marginBottom: 8 }}>
        图层（共 {components.length}）
      </Text>
      <Input
        size="small"
        allowClear
        prefix={<SearchOutlined style={{ fontSize: 11 }} />}
        placeholder="搜索组件"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
      />
      {sorted.length === 0 && <Empty description="无匹配组件" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
      {sorted.map((c, i) => {
        const selected = c.id === selectedComponentId;
        const visible = (c.config?.visible ?? c.base?.visible ?? true) === true;
        const locked = c.config?.locked === true;
        const dragging = drag?.id === c.id;
        return (
          <div key={c.id}>
            {/* 插入指示线（拖动时显示：第 i 行上方 / 末尾） */}
            {drag && drag.overIndex === i && (
              <div data-testid="layer-drop-indicator" style={{ height: 2, background: '#1677ff', borderRadius: 1, margin: '1px 0' }} />
            )}
            <div
              data-testid="layer-row"
              ref={(node) => { rowRefs.current[c.id] = node; }}
              onMouseDown={(e) => onRowMouseDown(e, c.id)}
              onClick={() => {
                // 拖拽结束的合成 click 不触发选中（拖拽本身已把该行保持在选中态）
                if (suppressClickRef.current) return;
                selectComponent(c.id);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px',
                borderRadius: 4, cursor: search.trim() ? 'pointer' : 'grab', marginBottom: 2,
                background: selected ? 'rgba(22,119,255,0.18)' : dragging ? 'rgba(22,119,255,0.10)' : 'transparent',
                border: `1px solid ${selected ? '#1677ff' : 'transparent'}`,
                opacity: dragging ? 0.55 : visible ? 1 : 0.45,
                userSelect: 'none',
              }}
            >
              {/* 2026-10-13（用户反馈）：去除序号/类型图标/上移下移置顶置底按钮；眼睛前置（可拖拽排序） */}
              <Button
                size="small"
                type="text"
                title={visible ? '隐藏' : '显示'}
                aria-label="显示"
                style={{ flexShrink: 0, padding: '0 4px', height: 22, minWidth: 22 }}
                icon={visible ? <EyeOutlined /> : <EyeInvisibleOutlined style={{ color: '#8b8ba7' }} />}
                onClick={() => toggleVisible(c)}
              />
              <span style={{ flex: 1, fontSize: 12, color: '#ccc', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                {editingId === c.id ? (
                  <Input
                    size="small"
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => commitRename(c.id)}
                    onPressEnter={() => commitRename(c.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ padding: '0 4px', fontSize: 12 }}
                  />
                ) : (
                  <span
                    onDoubleClick={(e) => { e.stopPropagation(); setEditingId(c.id); setRenameValue(nameOf(c)); }}
                    title="双击重命名"
                  >
                    {nameOf(c)}
                  </span>
                )}
              </span>
              <Space size={2} onClick={(e) => e.stopPropagation()}>
                <Button size="small" type="text" title="锁定" aria-label="锁定"
                  icon={locked ? <LockOutlined style={{ color: '#faad14' }} /> : <UnlockOutlined />}
                  onClick={() => toggleLock(c)} />
                <Button size="small" type="text" danger title="删除" aria-label="删除" icon={<DeleteOutlined />} onClick={() => removeComponent(c.id)} />
              </Space>
            </div>
          </div>
        );
      })}
      {drag && drag.overIndex === sorted.length && (
        <div data-testid="layer-drop-indicator" style={{ height: 2, background: '#1677ff', borderRadius: 1, margin: '1px 0' }} />
      )}
      <Text style={{ fontSize: 10, color: '#555', display: 'block', marginTop: 6 }}>
        按住行左键上下拖动调整层级；双击名称重命名；列表顶部 = 最顶层
      </Text>
    </div>
  );
}
